import { translate } from "app/language/service"
import { NOT_SAVED_ID } from "app/model"
import {
  GEO_TARGETS_TYPES,
  IReportCoordinateRow,
  IReportCreativeRow,
  IReportSSPOperatorRow,
  IReportShapeRow
} from "form/model"
import { IReportsShapeResult } from "reports/model"
import { reportsRPO } from "reports/repository"
import { getSspNameFromId } from "ssp/tools/common"

export async function getSSPOperatorReportRows(campaignId: number): Promise<IReportSSPOperatorRow[] | string> {
  try {
    const reports = await reportsRPO.getSSPOperatorResults(campaignId)
    return reports.map(j => ({
      sspId: j.ssp_id || 0,
      sspName: getSspNameFromId(j.ssp_id || 0),
      operatorName: j.publisher_id || "",
      spend: j.spend,
      cpm: j.ecpm,
      creatives: j.media_count || 0,
      impressions_with_multiplier: j.impressions,
      impressions_without_multiplier: j.buys
    }))
  } catch (error) {
    console.error(error)
    return translate("form.report.error.unknown")
  }
}

export async function getCreativesReportRows(campaignId: number): Promise<IReportCreativeRow[] | string> {
  try {
    const reports = await reportsRPO.getCreativeResults(campaignId)
    return reports.map(j => ({
      id: (j as any).id,
      name: j.media_name || "",
      impressions_with_multiplier: j.impressions,
      impressions_without_multiplier: j.buys,
      screens: j.screen_count || 0,
      type: j.media_is_video ? "video" : "image",
      dimension: j.media_dimension || ""
    }))
  } catch (error) {
    console.error(error)
    return translate("form.report.error.unknown")
  }
}

export async function getShapesReportRows(campaignId: number): Promise<IReportShapeRow[] | string> {
  try {
    const reports = await reportsRPO.getShapeResults(campaignId)
    return reports.map((j, index) => {
      const [type, name] = getShapeInfo(j)
      return {
        id: index,
        type,
        name,
        spend: j.spend,
        cpm: j.ecpm,
        impressions_with_multiplier: j.impressions,
        impressions_without_multiplier: j.buys,
        screens: j.screen_count || 0,
        venueTypeId: typeof j.venue_type_id === "number" ? j.venue_type_id : NOT_SAVED_ID,
        venueTypeName: j.venue_type_name || ""
      }
    })
  } catch (error) {
    console.error(error)
    return translate("form.report.error.unknown")
  }
}

export async function getCoordinatesReportRows(campaignId: number): Promise<IReportCoordinateRow[] | string> {
  try {
    const reports = await reportsRPO.getCoordinateResults(campaignId)
    return reports.map(j => ({
      id: j.campaign_coordinate_id || -1,
      latitude: j.campaign_coordinate_lat || 0,
      longitude: j.campaign_coordinate_lon || 0,
      radius: j.campaign_coordinate_radius || 0,
      name: j.campaign_coordinate_name || translate("label.no.coordinate"),
      spend: j.spend,
      cpm: j.ecpm,
      impressions_with_multiplier: j.impressions,
      impressions_without_multiplier: j.buys
    }))
  } catch (error) {
    console.error(error)
    return translate("form.report.error.unknown")
  }
}

function getShapeInfo({ city, postal_code, state, country }: IReportsShapeResult): [GEO_TARGETS_TYPES, string] {
  let name: string = ""
  if (city) {
    name = `${city} - `
  }
  if (postal_code) {
    name += `${postal_code} - `
  }
  if (state) {
    name += `${state} - `
  }
  if (country) {
    name += `${country} - `
  }
  if (name.length > 3) {
    name = name.substring(0, name.length - 3)
  }
  if (city) {
    return [GEO_TARGETS_TYPES.CITY, name]
  }
  if (postal_code) {
    return [GEO_TARGETS_TYPES.POSTAL_CODE, name]
  }
  if (state) {
    return [GEO_TARGETS_TYPES.STATE, name]
  }
  return [GEO_TARGETS_TYPES.COUNTRY, name]
}
